import { Suspense, useEffect, useState } from 'react';
import {
  Bell,
  CalendarDays,
  CheckSquare2,
  ChevronRight,
  CircleDollarSign,
  House,
  LogOut,
  Menu,
  MessageCircleMore,
  Search,
  Settings,
  Stethoscope,
  UsersRound,
  X,
  type LucideIcon,
} from 'lucide-react';
import {
  Badge,
  Box,
  ButtonBase,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Menu as MuiMenu,
  Snackbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { TextField } from '../shared/ui/TextField';
import { Stack } from '../shared/ui/Stack';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { useAuth } from './AuthProvider';
import { useClinicData } from '../data/ClinicDataProvider';
import type { PatientSearchResult } from '../firebase/clinicRepository';
import { roleLabels } from '../domain/formatters';
import { visibleUnreadNotificationCount } from '../domain/notifications';
import { tokens } from '../theme/tokens';
import { UserAvatar } from '../shared/ui/UserAvatar';

interface NavigationItem {
  label: string;
  path: string;
  icon: LucideIcon;
}

const primaryNavigation: NavigationItem[] = [
  { label: 'Início', path: '/', icon: House },
  { label: 'Agenda', path: '/agenda', icon: CalendarDays },
  { label: 'Pacientes', path: '/pacientes', icon: UsersRound },
  { label: 'Financeiro', path: '/financeiro', icon: CircleDollarSign },
  { label: 'Tarefas', path: '/tarefas', icon: CheckSquare2 },
  { label: 'Conversas', path: '/conversas', icon: MessageCircleMore },
];

function navigationLabel(pathname: string): string {
  const primaryItem = primaryNavigation.find((item) =>
    item.path === '/' ? pathname === '/' : pathname.startsWith(item.path),
  );
  if (primaryItem) return primaryItem.label;
  if (pathname.startsWith('/notificacoes')) return 'Notificações';
  if (pathname.startsWith('/configuracoes')) return 'Configurações';
  return 'página';
}

function PageLoadingFallback({ pathname }: { pathname: string }) {
  const label = navigationLabel(pathname);
  return (
    <Box
      sx={{ minHeight: 280, display: 'grid', placeItems: 'center' }}
      role="status"
      aria-live="polite"
      aria-label={`Carregando ${label}`}
    >
      <Stack alignItems="center" gap={2}>
        <CircularProgress size={30} />
        <Typography variant="body2" color="text.secondary">
          Carregando {label.toLocaleLowerCase('pt-BR')}…
        </Typography>
      </Stack>
    </Box>
  );
}

function Brand() {
  return (
    <Box sx={{ px: 4, py: 5, display: 'flex', alignItems: 'center', gap: 3 }}>
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: 3,
          display: 'grid',
          placeItems: 'center',
          color: 'white',
          background: `linear-gradient(145deg, ${tokens.color.accent}, ${tokens.color.accentStrong})`,
        }}
      >
        <Stethoscope size={19} aria-hidden="true" />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 750, lineHeight: 1.15 }}>Priscilla Falcão</Typography>
        <Typography variant="caption">Psiquiatra</Typography>
      </Box>
    </Box>
  );
}

export function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { data, loading, notice, clearNotice, searchPatients } = useClinicData();
  const desktop = useMediaQuery('(min-width:1024px)');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchResults, setSearchResults] = useState<PatientSearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [profileAnchor, setProfileAnchor] = useState<HTMLElement | null>(null);
  const [signingOut, setSigningOut] = useState(false);
  const [signOutError, setSignOutError] = useState('');
  const [pendingPath, setPendingPath] = useState<string | null>(null);

  const displayedPath = pendingPath ?? location.pathname;
  const pendingLabel = pendingPath ? navigationLabel(pendingPath) : null;

  const unreadNotifications = visibleUnreadNotificationCount(data.notifications, loading);
  const profileUser = data.users.find((candidate) => candidate.id === user?.id) ?? user;
  const unreadChat =
    user && !loading
      ? data.conversations.reduce(
          (sum, conversation) => sum + (conversation.unreadByUser[user.id] ?? 0),
          0,
        )
      : 0;
  useEffect(() => {
    const normalizedLength = search.trim().length;
    if (normalizedLength < 2) return undefined;
    let active = true;
    const timer = window.setTimeout(() => {
      void searchPatients(search)
        .then((result) => {
          if (active) setSearchResults(result.items.slice(0, 6));
        })
        .catch(() => {
          if (active) setSearchResults([]);
        })
        .finally(() => {
          if (active) setSearchLoading(false);
        });
    }, 280);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [search, searchPatients]);

  useEffect(() => {
    if (pendingPath !== location.pathname) return undefined;
    const timer = window.setTimeout(() => {
      setPendingPath((currentPath) => (currentPath === location.pathname ? null : currentPath));
    }, 0);
    return () => window.clearTimeout(timer);
  }, [location.pathname, pendingPath]);

  function navigateTo(path: string) {
    if (path !== location.pathname) setPendingPath(path);
    navigate(path);
    setDrawerOpen(false);
  }

  async function handleSignOut() {
    setSigningOut(true);
    setSignOutError('');
    try {
      await signOut();
    } catch {
      setSigningOut(false);
      setSignOutError('Não foi possível sair agora. Tente novamente.');
    }
  }

  const navigationContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Brand />
      <Box sx={{ px: 3 }}>
        <Typography
          variant="caption"
          sx={{
            px: 2,
            mb: 2,
            display: 'block',
            fontWeight: 720,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}
        >
          Consultório
        </Typography>
        <List component="nav" aria-label="Seções do consultório" disablePadding>
          {primaryNavigation.map((item) => {
            const active =
              item.path === '/' ? displayedPath === '/' : displayedPath.startsWith(item.path);
            const current =
              item.path === '/'
                ? location.pathname === '/'
                : location.pathname.startsWith(item.path);
            const pending = pendingPath === item.path;
            const count = item.path === '/conversas' ? unreadChat : 0;
            return (
              <ListItemButton
                key={item.path}
                selected={active}
                aria-current={current ? 'page' : undefined}
                aria-busy={pending || undefined}
                onClick={() => navigateTo(item.path)}
                sx={{
                  minHeight: 44,
                  borderRadius: 3,
                  mb: 1,
                  color: active ? 'primary.main' : 'text.secondary',
                  '&.Mui-selected': { backgroundColor: 'primary.light' },
                  '&.Mui-selected:hover': { backgroundColor: 'primary.light' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>
                  <item.icon size={19} aria-hidden="true" />
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  slotProps={{ primary: { sx: { fontSize: 14, fontWeight: active ? 720 : 600 } } }}
                />
                {pending ? (
                  <CircularProgress size={17} aria-hidden="true" />
                ) : count > 0 ? (
                  <Box
                    sx={{
                      minWidth: 22,
                      height: 22,
                      px: 1,
                      borderRadius: 10,
                      display: 'grid',
                      placeItems: 'center',
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      fontSize: 11,
                      fontWeight: 750,
                    }}
                  >
                    {count}
                  </Box>
                ) : null}
              </ListItemButton>
            );
          })}
        </List>
      </Box>
      <Box sx={{ mt: 'auto', p: 3 }}>
        <ListItemButton
          onClick={() => navigateTo('/configuracoes')}
          selected={displayedPath.startsWith('/configuracoes')}
          aria-current={location.pathname.startsWith('/configuracoes') ? 'page' : undefined}
          aria-busy={pendingPath === '/configuracoes' || undefined}
          sx={{ borderRadius: 3, color: 'text.secondary' }}
        >
          <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>
            {pendingPath === '/configuracoes' ? (
              <CircularProgress size={17} aria-hidden="true" />
            ) : (
              <Settings size={19} aria-hidden="true" />
            )}
          </ListItemIcon>
          <ListItemText
            primary="Configurações"
            slotProps={{ primary: { sx: { fontSize: 14, fontWeight: 600 } } }}
          />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh' }}>
      {desktop ? (
        <Drawer
          variant="permanent"
          sx={{
            width: tokens.sidebarWidth,
            '& .MuiDrawer-paper': {
              width: tokens.sidebarWidth,
              bgcolor: 'background.paper',
            },
          }}
        >
          {navigationContent}
        </Drawer>
      ) : (
        <Drawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          sx={{ '& .MuiDrawer-paper': { width: 'min(86vw, 300px)' } }}
        >
          <Box sx={{ position: 'absolute', right: 8, top: 8 }}>
            <IconButton aria-label="Fechar menu" onClick={() => setDrawerOpen(false)}>
              <X size={20} />
            </IconButton>
          </Box>
          {navigationContent}
        </Drawer>
      )}

      <Box sx={{ ml: desktop ? `${tokens.sidebarWidth}px` : 0 }}>
        <Box
          component="header"
          sx={{
            minHeight: 72,
            px: { xs: 3, sm: 5, lg: 8 },
            display: 'flex',
            alignItems: 'center',
            gap: 3,
            position: 'sticky',
            top: 0,
            zIndex: 20,
            bgcolor: tokens.overlay.canvasGlass,
            backdropFilter: 'blur(14px)',
            borderBottom: `1px solid ${tokens.color.border}`,
          }}
        >
          {!desktop ? (
            <IconButton aria-label="Abrir menu" onClick={() => setDrawerOpen(true)}>
              <Menu size={21} />
            </IconButton>
          ) : null}
          <Box sx={{ position: 'relative', flex: 1, maxWidth: 560 }}>
            <TextField
              fullWidth
              placeholder="Buscar paciente por nome, CPF ou telefone"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setSearchLoading(event.target.value.trim().length >= 2);
                setSearchOpen(true);
              }}
              onFocus={() => setSearchOpen(true)}
              inputProps={{ 'aria-label': 'Buscar pacientes' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} aria-hidden="true" />
                  </InputAdornment>
                ),
                endAdornment: searchLoading ? (
                  <InputAdornment position="end">
                    <CircularProgress size={18} />
                  </InputAdornment>
                ) : undefined,
              }}
              sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}
            />
            {searchOpen && search.trim() ? (
              <Box
                role="listbox"
                aria-label="Resultados da busca"
                sx={{
                  position: 'absolute',
                  top: 'calc(100% + 8px)',
                  left: 0,
                  right: 0,
                  bgcolor: 'background.paper',
                  border: `1px solid ${tokens.color.border}`,
                  borderRadius: 3,
                  boxShadow: tokens.shadow.floating,
                  p: 1.5,
                  zIndex: 30,
                }}
              >
                {searchLoading ? (
                  <Stack direction="row" alignItems="center" gap={2} sx={{ p: 3 }} role="status">
                    <CircularProgress size={18} />
                    <Typography variant="body2" color="text.secondary">
                      Buscando pacientes…
                    </Typography>
                  </Stack>
                ) : searchResults.length ? (
                  searchResults.map((patient) => (
                    <ButtonBase
                      key={patient.patientId}
                      role="option"
                      onClick={() => {
                        navigateTo(`/pacientes/${patient.patientId}`);
                        setSearch('');
                        setSearchLoading(false);
                        setSearchOpen(false);
                      }}
                      sx={{
                        width: '100%',
                        p: 2.5,
                        justifyContent: 'space-between',
                        borderRadius: 2.5,
                        textAlign: 'left',
                        '&:hover': { bgcolor: 'background.default' },
                      }}
                    >
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {patient.fullName}
                        </Typography>
                        <Typography variant="caption">
                          {patient.primaryPhone} • CPF final{' '}
                          {patient.cpf.replace(/\D/g, '').slice(-4)}
                        </Typography>
                      </Box>
                      <ChevronRight size={17} aria-hidden="true" />
                    </ButtonBase>
                  ))
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ p: 3, textAlign: 'center' }}
                  >
                    Nenhum paciente encontrado.
                  </Typography>
                )}
              </Box>
            ) : null}
          </Box>
          <Tooltip title="Notificações">
            <IconButton
              aria-label={`${unreadNotifications} notificações não lidas`}
              onClick={() => navigateTo('/notificacoes')}
            >
              <Badge badgeContent={unreadNotifications} color="error" max={9}>
                <Bell size={20} />
              </Badge>
            </IconButton>
          </Tooltip>
          <ButtonBase
            aria-label="Abrir menu do perfil"
            onClick={(event) => setProfileAnchor(event.currentTarget)}
            sx={{
              borderRadius: 3,
              p: 1,
              display: { xs: 'none', sm: 'flex' },
              gap: 2,
              textAlign: 'left',
            }}
          >
            <UserAvatar
              user={profileUser}
              sx={{
                width: 34,
                height: 34,
                fontSize: 13,
                fontWeight: 750,
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" noWrap sx={{ fontWeight: 700, maxWidth: 160 }}>
                {user?.displayName}
              </Typography>
              {user ? <Typography variant="caption">{roleLabels[user.role]}</Typography> : null}
            </Box>
          </ButtonBase>
          <MuiMenu
            anchorEl={profileAnchor}
            open={Boolean(profileAnchor)}
            onClose={() => setProfileAnchor(null)}
          >
            <MenuItem
              onClick={() => {
                setProfileAnchor(null);
                navigateTo('/configuracoes');
              }}
            >
              <Settings size={17} style={{ marginRight: 10 }} /> Configurações
            </MenuItem>
            <Divider />
            <MenuItem disabled={signingOut} onClick={() => void handleSignOut()}>
              {signingOut ? (
                <CircularProgress size={17} sx={{ mr: 2.5 }} />
              ) : (
                <LogOut size={17} style={{ marginRight: 10 }} />
              )}
              {signingOut ? 'Saindo…' : 'Sair com segurança'}
            </MenuItem>
          </MuiMenu>
          {pendingPath ? (
            <Box
              role="status"
              aria-live="polite"
              aria-label={`Abrindo ${pendingLabel}`}
              sx={{ position: 'absolute', left: 0, right: 0, bottom: 0 }}
            >
              <Box
                sx={{
                  height: 3,
                  bgcolor: 'primary.main',
                  transformOrigin: 'left',
                  animation: 'navigation-progress 900ms ease-in-out infinite alternate',
                  '@keyframes navigation-progress': {
                    from: { transform: 'scaleX(0.28)', opacity: 0.65 },
                    to: { transform: 'scaleX(0.82)', opacity: 1 },
                  },
                  '@media (prefers-reduced-motion: reduce)': {
                    animation: 'none',
                    transform: 'scaleX(1)',
                  },
                }}
              />
            </Box>
          ) : null}
        </Box>

        <Box
          component="main"
          onClick={() => setSearchOpen(false)}
          sx={{
            width: '100%',
            maxWidth: 1500,
            mx: 'auto',
            px: { xs: 3, sm: 5, lg: 8 },
            py: { xs: 5, lg: 8 },
            pb: { xs: 12, lg: 8 },
          }}
        >
          <Suspense fallback={<PageLoadingFallback pathname={displayedPath} />}>
            <Outlet />
          </Suspense>
        </Box>
      </Box>

      {!desktop ? (
        <Box
          component="nav"
          aria-label="Navegação principal"
          sx={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 25,
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            bgcolor: 'background.paper',
            borderTop: `1px solid ${tokens.color.border}`,
            pb: 'env(safe-area-inset-bottom)',
          }}
        >
          {primaryNavigation
            .slice(0, 3)
            .concat(primaryNavigation[4]!)
            .map((item) => {
              const active =
                item.path === '/' ? displayedPath === '/' : displayedPath.startsWith(item.path);
              const current =
                item.path === '/'
                  ? location.pathname === '/'
                  : location.pathname.startsWith(item.path);
              const pending = pendingPath === item.path;
              return (
                <ButtonBase
                  key={item.path}
                  onClick={() => navigateTo(item.path)}
                  aria-current={current ? 'page' : undefined}
                  aria-busy={pending || undefined}
                  sx={{
                    minHeight: 64,
                    flexDirection: 'column',
                    gap: 0.5,
                    color: active ? 'primary.main' : 'text.secondary',
                  }}
                >
                  {pending ? (
                    <CircularProgress size={17} aria-hidden="true" />
                  ) : (
                    <item.icon size={19} aria-hidden="true" />
                  )}
                  <Typography
                    variant="caption"
                    color="inherit"
                    sx={{ fontWeight: active ? 720 : 600 }}
                  >
                    {item.label}
                  </Typography>
                </ButtonBase>
              );
            })}
        </Box>
      ) : null}

      <Snackbar
        key={notice}
        open={Boolean(notice)}
        autoHideDuration={3500}
        onClose={clearNotice}
        message={notice}
        action={
          <IconButton size="small" color="inherit" aria-label="Fechar aviso" onClick={clearNotice}>
            <X size={17} />
          </IconButton>
        }
      />
      <Snackbar
        open={Boolean(signOutError)}
        autoHideDuration={5000}
        onClose={() => setSignOutError('')}
        message={signOutError}
      />
    </Box>
  );
}
